<template>
  <div
    class="form-group"
    :class="{
      'form-group--has-error': localError,
      'form-group--has-success': !localError && touched,
    }"
  >
    <label v-if="label" :for="id">{{ label }}</label>

    <!-- Compact Version -->
    <div v-if="compact" class="formula-editor-compact">
      <div class="compact-toolbar">
        <div class="toolbar-section operators-section">
          <button
            v-for="operator in operators.slice(0, 6)"
            :key="operator.symbol"
            @click.prevent.stop="insertOperatorCompact(operator.symbol)"
            class="toolbar-button compact"
            :title="operator.description"
          >
            {{ operator.symbol }}
          </button>
        </div>
        <div class="toolbar-section fields-section">
          <div class="fields-scroll">
            <button
              v-for="field in availableFields"
              :key="field.id"
              @click.prevent.stop="insertFieldCompact(field)"
              class="toolbar-button field-button"
              :title="`${field.label} (${field.type})`"
            >
              {{ field.label }}
            </button>
          </div>
        </div>
        <button
          class="toolbar-button more-operators"
          @click.prevent.stop="openModal"
          title="Open full editor"
        >
          <BaseIcon name="pencil" size="14" />
        </button>
      </div>
      <div class="compact-content" @click="openModal">
        <div class="compact-editor-area">
          <div v-if="!modelValue" class="placeholder">Click to edit formula...</div>
          <div v-else class="formula-preview" v-html="formatForDisplayHTML(modelValue)"></div>
        </div>
        <BaseIcon name="edit" size="16" class="edit-icon" />
      </div>
    </div>

    <!-- Full Version -->
    <div v-else class="formula-editor" :class="{ 'formula-editor--has-error': localError }">
      <div class="editor-toolbar">
        <div class="toolbar-section">
          <button
            v-for="operator in operators"
            :key="operator.symbol"
            @click.prevent="insertOperator(operator.symbol)"
            class="toolbar-button"
            :title="operator.description"
          >
            {{ operator.symbol }}
          </button>
        </div>
        <div class="toolbar-section fields-section">
          <input
            v-if="availableFields.length > 10"
            v-model="fieldSearchQuery"
            type="text"
            placeholder="Search fields..."
            class="field-search"
            @click.stop
          />
          <div class="fields-grid">
            <button
              v-for="field in filteredFields"
              :key="field.id"
              @click.prevent="insertField(field)"
              class="toolbar-button field-button"
              :title="`${field.label} (${field.type})`"
            >
              {{ field.label }}
            </button>
          </div>
          <button v-if="hasMoreFields" @click.prevent="loadMoreFields" class="load-more-button">
            Read more ({{ availableFields.length - visibleFieldsCount }} more fields)
          </button>
        </div>
      </div>
      <div ref="editorRef" class="editor-content" />
    </div>
    <span v-if="localError" class="form-group__error">{{ localError }}</span>
  </div>
</template>

<script>
  import { defineComponent, ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
  import { Editor } from '@tiptap/vue-3';
  import StarterKit from '@tiptap/starter-kit';
  import { Extension, Node } from '@tiptap/core';
  import { useFormStore } from '@/store/formStore';
  import { useUIStore } from '@/store/uiStore';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  const ALLOWED_TYPES = [
    'number',
    'slider',
    'single-choice',
    'multiple-choice',
    'picture-choice',
    'dropdown',
    'formula',
  ];

  const FieldNode = Node.create({
    name: 'fieldNode',
    group: 'inline',
    inline: true,
    atom: true,

    addAttributes() {
      return {
        id: {
          default: null,
        },
        label: {
          default: null,
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: 'span[data-field-id]',
        },
      ];
    },

    renderHTML({ node }) {
      return [
        'span',
        {
          class: 'field-tag-wrapper',
          'data-field-label': node.attrs.label,
        },
        [
          'span',
          {
            class: 'field-tag',
            'data-field-id': node.attrs.id,
          },
          node.attrs.label,
        ],
      ];
    },

    toText({ node }) {
      return `{${node.attrs.id}}`;
    },
  });

  const FieldTag = Extension.create({
    name: 'fieldTag',
    addGlobalAttributes() {
      return [
        {
          types: ['span'],
          attributes: {
            'data-field-id': {
              default: null,
              parseHTML: (element) => element.getAttribute('data-field-id'),
              renderHTML: (attributes) => {
                if (!attributes['data-field-id']) {
                  return {};
                }
                return { 'data-field-id': attributes['data-field-id'] };
              },
            },
            'data-field-label': {
              default: null,
              parseHTML: (element) => element.getAttribute('data-field-label'),
              renderHTML: (attributes) => {
                if (!attributes['data-field-label']) {
                  return {};
                }
                return { 'data-field-label': attributes['data-field-label'] };
              },
            },
          },
        },
      ];
    },
  });

  export default defineComponent({
    name: 'FormulaEditor',

    components: {
      BaseIcon,
    },

    props: {
      modelValue: {
        type: String,
        default: '',
      },
      label: {
        type: String,
        default: '',
      },
      error: {
        type: String,
        default: '',
      },
      id: {
        type: String,
        default: () => `formula-${Math.random().toString(36).substr(2, 9)}`,
      },
      compact: {
        type: Boolean,
        default: false,
      },
      customFields: {
        type: Array,
        default: null,
        description:
          'Custom fields to use instead of formStore elements (for product options, etc.)',
      },
      showConditionalOperators: {
        type: Boolean,
        default: true,
        description: 'Show IF/THEN/ELSE operators (hide for product pricing rules)',
      },
    },

    emits: ['update:modelValue', 'error'],

    setup(props, { emit }) {
      const formStore = useFormStore();
      const uiStore = useUIStore();
      const editorRef = ref(null);
      const localError = ref(props.error);
      const touched = ref(false);
      const fieldSearchQuery = ref('');
      const visibleFieldsCount = ref(12);
      let editor = null;

      const operators = computed(() => {
        const baseOperators = [
          { symbol: '+', description: 'Add' },
          { symbol: '-', description: 'Subtract' },
          { symbol: '*', description: 'Multiply' },
          { symbol: '/', description: 'Divide' },
          { symbol: '(', description: 'Open parenthesis' },
          { symbol: ')', description: 'Close parenthesis' },
          { symbol: '>', description: 'Greater than' },
          { symbol: '<', description: 'Less than' },
          { symbol: '=', description: 'Equals' },
        ];

        if (props.showConditionalOperators) {
          baseOperators.splice(
            4,
            0,
            { symbol: 'IF', description: 'Start conditional' },
            { symbol: 'THEN', description: 'Then clause' },
            { symbol: 'ELSE', description: 'Else clause' }
          );
        }

        return baseOperators;
      });

      const availableFields = computed(() => {
        // Use customFields if provided (for product options, etc.)
        if (props.customFields && Array.isArray(props.customFields)) {
          return props.customFields.map((field) => ({
            id: field.id || field.key,
            label: field.label || `Field ${field.id || field.key}`,
            type: field.type || 'number',
            choices: field.choices || null,
          }));
        }

        // Default: use formStore elements
        return formStore
          .getAllElements()
          .filter((element) => ALLOWED_TYPES.includes(element.type))
          .map((element) => ({
            id: element.id,
            label: element.props?.label || `Field ${element.id}`,
            type: element.type,
            choices: element.type.includes('choice') ? element.choices : null,
          }));
      });

      const filteredFields = computed(() => {
        let fields = availableFields.value;
        console.log('filteredFields computation:', {
          totalFields: availableFields.value.length,
          searchQuery: fieldSearchQuery.value,
          visibleCount: visibleFieldsCount.value,
        });

        // Apply search filter if there's a query
        if (fieldSearchQuery.value) {
          fields = fields.filter((field) =>
            field.label.toLowerCase().includes(fieldSearchQuery.value.toLowerCase())
          );
          // When searching, show all results
          console.log('Searching, returning all:', fields.length);
          return fields;
        }

        // When not searching and there are more than 10 fields, limit to visibleFieldsCount
        if (availableFields.value.length > 10) {
          const sliced = fields.slice(0, visibleFieldsCount.value);
          console.log('Limiting fields:', sliced.length, 'of', fields.length);
          return sliced;
        }

        // If 10 or fewer fields, show all
        console.log('Showing all fields (<=10):', fields.length);
        return fields;
      });

      const hasMoreFields = computed(() => {
        const hasMore =
          !fieldSearchQuery.value &&
          availableFields.value.length > 10 &&
          visibleFieldsCount.value < availableFields.value.length;
        console.log('hasMoreFields check:', {
          searchQuery: fieldSearchQuery.value,
          availableFieldsLength: availableFields.value.length,
          visibleFieldsCount: visibleFieldsCount.value,
          hasMore,
        });
        return hasMore;
      });

      const loadMoreFields = () => {
        visibleFieldsCount.value += 12;
      };

      const insertOperator = (operator) => {
        editor.chain().focus().insertContent(` ${operator} `).run();
        touched.value = true;
        validateFormula(getFormulaContent());
      };

      const insertField = (field) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'fieldNode',
            attrs: {
              id: field.id,
              label: field.label,
            },
          })
          .insertContent(' ')
          .run();
        touched.value = true;
        validateFormula(getFormulaContent());
      };

      const getFormulaContent = () => {
        let content = '';

        // Traverse all nodes in the document
        editor.state.doc.descendants((node) => {
          if (node.type.name === 'fieldNode') {
            // For field nodes, use the ID format
            content += `{${node.attrs.id}}`;
          } else if (node.text) {
            // For text nodes, add the text content
            content += node.text;
          }
        });

        // Normalize Dutch decimal commas to periods so the formula remains
        // valid mathjs syntax. Field nodes are emitted as `{id}` and don't
        // contain commas, so this is safe.
        return content.trim().replace(/,/g, '.');
      };

      const formatInitialContent = (content) => {
        if (!content) return '';
        // Normalize legacy Dutch decimal commas to periods before parsing.
        content = content.replace(/,/g, '.');
        console.log(content);
        // Wrap the content in a paragraph tag and handle field replacements
        const formattedContent = content.replace(/\{(\w+)\}|([^{}]+)/g, (match, fieldId, text) => {
          if (fieldId) {
            const field = availableFields.value.find((f) => f.id === fieldId);
            if (!field) return match;

            return {
              type: 'fieldNode',
              attrs: {
                id: fieldId,
                label: field.label,
              },
            };
          }
          return text;
        });
        console.log(formattedContent);

        // Ensure content is wrapped in a paragraph
        return formattedContent;
      };

      const validateFormula = (formula) => {
        try {
          if (!formula.trim()) {
            throw new Error('Formula cannot be empty');
          }

          const fieldMatches = formula.match(/\{(\w+)\}/g) || [];
          for (const match of fieldMatches) {
            const fieldId = match.slice(1, -1); // Remove { }
            if (!availableFields.value.find((f) => f.id === fieldId)) {
              throw new Error(`Invalid field reference: ${match}`);
            }
          }

          // Check for balanced parentheses
          const openParens = (formula.match(/\(/g) || []).length;
          const closeParens = (formula.match(/\)/g) || []).length;
          if (openParens !== closeParens) {
            throw new Error('Unmatched parentheses');
          }

          localError.value = '';
          emit('error', '');
          return true;
        } catch (error) {
          localError.value = error.message;
          emit('error', error.message);
          return false;
        }
      };

      // Only initialize editor if not in compact mode
      if (!props.compact) {
        onMounted(() => {
          // First initialize the editor with empty content
          editor = new Editor({
            element: editorRef.value,
            extensions: [StarterKit, FieldNode, FieldTag],
            content: {
              type: 'doc',
              content: [
                {
                  type: 'paragraph',
                  content: [],
                },
              ],
            },
            editorProps: {
              // Live-replace commas with periods so users can type Dutch-style
              // decimals (2,5) and still produce valid mathjs expressions (2.5).
              handleKeyDown: (_view, event) => {
                if (event.key === ',' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                  event.preventDefault();
                  editor.chain().focus().insertContent('.').run();
                  return true;
                }
                return false;
              },
              // Catch composed/IME input and pasted content with commas as well.
              handleTextInput: (_view, _from, _to, text) => {
                if (text && text.includes(',')) {
                  editor.chain().focus().insertContent(text.replace(/,/g, '.')).run();
                  return true;
                }
                return false;
              },
            },
            onUpdate: ({ editor }) => {
              const formula = getFormulaContent();
              if (validateFormula(formula)) {
                emit('update:modelValue', formula);
              }
            },
          });

          // Then, if we have initial content, insert it using the same pattern as insertField
          if (props.modelValue) {
            // Normalize Dutch decimal commas before tokenizing.
            const normalizedValue = props.modelValue.replace(/,/g, '.');
            // Verbeterde regex: splitst op veld-tags, operators en spaties, alles los
            const parts =
              normalizedValue.match(
                /(\{[\w-]+\}|[+\-*/()<>]=?|IF|THEN|ELSE|\s+|[^+\-*/()<>={}\s]+)/g
              ) || [];
            for (const part of parts) {
              const fieldMatch = part.match(/^\{([\w-]+)\}$/);
              if (fieldMatch) {
                const fieldId = fieldMatch[1];
                const field = availableFields.value.find((f) => f.id === fieldId);
                if (field) {
                  editor
                    .chain()
                    .insertContent({
                      type: 'fieldNode',
                      attrs: {
                        id: field.id,
                        label: field.label,
                      },
                    })
                    .run();
                }
              } else {
                editor.chain().insertContent(part).run();
              }
            }
          }
        });
      }

      watch(
        () => props.modelValue,
        (newValue) => {
          if (editor && newValue !== getFormulaContent()) {
            editor.commands.setContent(formatInitialContent(newValue));
          }
        }
      );

      watch(
        () => props.error,
        (newError) => {
          localError.value = newError;
        }
      );

      const formatForDisplay = (formula) => {
        if (!formula) return '';
        // Replace field IDs with field labels for display
        let displayFormula = formula;
        availableFields.value.forEach((field) => {
          const regex = new RegExp(`\\{${field.id}\\}`, 'g');
          displayFormula = displayFormula.replace(regex, field.label);
        });
        return displayFormula;
      };

      const formatForDisplayHTML = (formula) => {
        if (!formula) return '';

        // First, replace field IDs with styled field labels (only if field exists and has label)
        let displayFormula = formula;
        availableFields.value.forEach((field) => {
          if (field.label && field.label.trim()) {
            const regex = new RegExp(`\\{${field.id}\\}`, 'g');
            displayFormula = displayFormula.replace(
              regex,
              `<span class="field-tag">${field.label}</span>`
            );
          }
        });

        // Split into tokens: field-tags, operators, and words
        const tokens = [];
        const tokenRegex =
          /(<span class="field-tag">[^<]*<\/span>|[+\-*/()=<>]|IF|THEN|ELSE|\w+|\s+)/g;
        let match;

        while ((match = tokenRegex.exec(displayFormula)) !== null) {
          const token = match[1];
          if (token.trim()) {
            // Skip empty tokens
            tokens.push({
              content: token,
              isFieldTag: token.startsWith('<span class="field-tag">'),
              isOperator: /^[+\-*/()=<>]$|^(IF|THEN|ELSE)$/.test(token.trim()),
              isSpace: /^\s+$/.test(token),
              textLength: token.startsWith('<span class="field-tag">')
                ? token.replace(/<[^>]*>/g, '').length // Extract text content for length calculation
                : token.length,
            });
          }
        }

        const lines = [];
        let currentLine = '';
        let currentLineLength = 0;
        const maxCharsPerLine = 40;
        const maxLines = 4;

        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i];

          // Skip spaces at the beginning of lines
          if (token.isSpace && currentLine === '') {
            continue;
          }

          // Check if adding this token would exceed line length
          if (currentLineLength + token.textLength > maxCharsPerLine && currentLine.length > 0) {
            // Don't break in the middle of a field tag or after an operator
            if (!token.isSpace && !token.isOperator) {
              lines.push(currentLine.trim());

              if (lines.length >= maxLines - 1) {
                // We're at max lines, add ellipsis and break
                lines.push('...');
                break;
              }

              currentLine = token.content;
              currentLineLength = token.textLength;
            } else {
              // Add the token to current line if it's a space or operator
              currentLine += token.content;
              currentLineLength += token.textLength;
            }
          } else {
            currentLine += token.content;
            currentLineLength += token.textLength;
          }
        }

        // Add remaining content if we haven't reached max lines
        if (lines.length < maxLines && currentLine.trim()) {
          lines.push(currentLine.trim());
        }

        return lines.join('<br>');
      };

      const insertOperatorCompact = (operator) => {
        const newValue = ((props.modelValue || '') + ` ${operator} `).replace(/,/g, '.');
        emit('update:modelValue', newValue);
        touched.value = true;
      };

      const insertFieldCompact = (field) => {
        const fieldRef = `{${field.id}}`;
        const newValue = ((props.modelValue || '') + ` ${fieldRef} `).replace(/,/g, '.');
        emit('update:modelValue', newValue);
        touched.value = true;
      };

      const openModal = async () => {
        // Dynamically import the modal component to avoid circular dependency
        const { default: FormulaEditorModal } = await import('./FormulaEditorModal.vue');

        const modalId = uiStore.addModal(FormulaEditorModal, {
          title: props.label || 'Formula Editor',
          size: 'xl',
          hasComponent: true,
          modelValue: props.modelValue,
          label: props.label,
          error: props.error,
          id: props.id,
          customFields: props.customFields,
          showConditionalOperators: props.showConditionalOperators,
          onUpdate: (value) => {
            emit('update:modelValue', value);
          },
          onError: (error) => {
            emit('error', error);
          },
          onClose: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      onBeforeUnmount(() => {
        if (editor) {
          editor.destroy();
        }
      });

      return {
        editorRef,
        operators,
        availableFields,
        filteredFields,
        fieldSearchQuery,
        visibleFieldsCount,
        hasMoreFields,
        loadMoreFields,
        insertOperator,
        insertField,
        insertOperatorCompact,
        insertFieldCompact,
        localError,
        touched,
        formatForDisplay,
        formatForDisplayHTML,
        openModal,
      };
    },
  });
</script>

<style lang="scss" scoped>
  .formula-editor {
    border: 1px solid var(--slate-200);
    border-radius: 0.375rem;
    overflow: hidden;

    &--has-error {
      border-color: var(--red-500);
    }

    .editor-toolbar {
      display: flex;
      gap: 1rem;
      padding: 0.5rem;
      border-bottom: 1px solid var(--slate-200);
      background: var(--slate-50);
      flex-wrap: wrap;

      .toolbar-section {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;

        &.fields-section {
          flex-direction: column;
          width: 100%;
          gap: 0.75rem;

          .field-search {
            padding: 0.5rem;
            border: 1px solid var(--slate-200);
            border-radius: 0.25rem;
            font-size: 0.875rem;
            background: white;

            &:focus {
              outline: none;
              border-color: var(--brand-300);
              box-shadow: 0 0 0 3px rgb(var(--brand-50));
            }
          }

          .fields-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 0.5rem;
          }

          .load-more-button {
            padding: 0.5rem 1rem;
            border: 1px solid var(--brand-200);
            background: var(--brand-50);
            color: var(--brand-600);
            border-radius: 0.25rem;
            cursor: pointer;
            font-size: 0.875rem;
            text-align: center;
            transition: all 0.2s ease;

            &:hover {
              background: var(--brand-100);
              border-color: var(--brand-300);
            }
          }
        }
      }
    }

    .toolbar-button {
      padding: 0.25rem 0.5rem;
      border: 1px solid var(--slate-200);
      background: white;
      border-radius: 0.25rem;
      cursor: pointer;
      color: var(--slate-600);
      font-size: 0.875rem;

      &:hover {
        background: var(--slate-100);
      }

      &.field-button {
        background: var(--brand-50);
        border-color: var(--brand-200);
        color: var(--brand-600);

        &:hover {
          background: var(--brand-100);
        }
      }
    }

    .editor-content {
      padding: 0.75rem;
      min-height: 100px;
      background: white;
      overflow-x: auto;

      :deep(.ProseMirror) {
        outline: none;

        > * + * {
          margin-top: 0.75em;
        }

        p.is-empty::before {
          content: 'Enter your formula here...';
          float: left;
          color: var(--slate-400);
          pointer-events: none;
          height: 0;
        }

        .field-tag {
          background: var(--brand-50);
          border: 1px solid var(--brand-200);
          color: var(--brand-600);
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
          white-space: nowrap;
          display: inline-block;
          margin: 0 0.125rem;
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
          position: relative;

          // Remove the hover styles from here and add a wrapper
          span {
            display: none;
          }
        }

        // Add new wrapper class
        .field-tag-wrapper {
          position: relative;
          display: inline-block;

          &:hover::after {
            content: attr(data-field-label);
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background: var(--slate-800);
            color: white;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            font-size: 0.75rem;
            white-space: nowrap;
            z-index: 10;
            opacity: 0;
            animation: fadeIn 0.2s ease-in-out 0.5s forwards;
          }
        }
      }
    }
  }
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, 0.5rem);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }

  .formula-editor-compact {
    border: 1px solid var(--slate-200);
    border-radius: 0.375rem;
    background: white;
    overflow: hidden;

    &:hover {
      border-color: var(--brand-300);
      box-shadow: 0 0 0 3px rgb(var(--brand-50));
    }

    .compact-toolbar {
      display: flex;
      gap: 0.5rem;
      padding: 0.5rem;
      border-bottom: 1px solid var(--slate-200);
      background: var(--slate-50);
      align-items: center;
      flex-wrap: wrap;

      .toolbar-section {
        display: flex;
        gap: 0.25rem;
        flex-wrap: wrap;
        align-items: center;

        &.operators-section {
          flex-shrink: 0;
        }

        &.fields-section {
          flex: 1;
          min-width: 0;
          overflow: hidden;

          .fields-scroll {
            display: flex;
            gap: 0.25rem;
            overflow-x: auto;
            overflow-y: hidden;
            flex-wrap: nowrap;
            scrollbar-width: thin;
            -webkit-overflow-scrolling: touch;

            &::-webkit-scrollbar {
              height: 4px;
            }

            &::-webkit-scrollbar-track {
              background: transparent;
            }

            &::-webkit-scrollbar-thumb {
              background: var(--slate-300);
              border-radius: 2px;
            }
          }
        }
      }

      .toolbar-button {
        padding: 0.25rem 0.5rem;
        border: 1px solid var(--slate-200);
        background: white;
        border-radius: 0.25rem;
        cursor: pointer;
        color: var(--slate-600);
        font-size: 0.75rem;
        min-width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: var(--slate-100);
        }

        &.compact {
          font-size: 0.75rem;
        }

        &.more-operators {
          background: var(--brand-50);
          border-color: var(--brand-200);
          color: var(--brand-600);
          flex-shrink: 0;

          &:hover {
            background: var(--brand-100);
          }
        }

        &.field-button {
          background: var(--brand-50);
          border-color: var(--brand-200);
          color: var(--brand-600);
          white-space: nowrap;
          flex-shrink: 0;

          &:hover {
            background: var(--brand-100);
          }
        }
      }
    }

    .compact-content {
      display: flex;
      align-items: flex-start;
      padding: 0.75rem;
      min-height: 80px;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background: var(--slate-25);
      }

      .compact-editor-area {
        flex: 1;
        margin-right: 0.5rem;

        .placeholder {
          color: var(--slate-400);
          font-style: italic;
        }

        .formula-preview {
          color: var(--slate-700);
          line-height: 1.4;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.875rem;
          display: flex;
          flex-wrap: wrap;

          :deep(.field-tag) {
            background: var(--brand-50);
            border: 1px solid var(--brand-200);
            color: var(--brand-600);
            padding: 0.125rem 0.375rem;
            border-radius: 0.25rem;
            font-size: 0.75rem;
            white-space: nowrap;
            display: inline-block;
            margin: 0 0.125rem;
          }
        }
      }

      .edit-icon {
        color: var(--slate-400);
        flex-shrink: 0;
        transition: color 0.2s ease;
        margin-top: 0.125rem;
      }
    }

    &:hover .edit-icon {
      color: var(--brand-500);
    }
  }
</style>
